import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CreditCard, ShoppingBag, Trophy } from "lucide-react";
import { api, fmtDate, ghs } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { EmptyState, StatusBadge } from "../../components/site/Bits";

const STORE_IMG = "https://images.unsplash.com/photo-1631205767853-13a8d5885488?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const PRED_IMG = "https://images.unsplash.com/photo-1676746424139-77f8bd8922a8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const BigCard = ({ to, img, eyebrow, title, text, accent, testId }) => (
  <Link to={to} data-testid={testId} className="group relative block min-h-[300px] overflow-hidden rounded-3xl border border-white/10 card-hover sm:min-h-[340px]">
    <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40 transition-transform duration-700 group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0b0f17] via-[#0b0f17]/80 to-[#0b0f17]/30" />
    <div className="relative flex h-full min-h-[300px] flex-col justify-end p-8 sm:min-h-[340px] sm:p-10">
      <span className={`text-xs font-semibold uppercase tracking-[0.2em] ${accent}`}>{eyebrow}</span>
      <h2 className="mt-3 font-display text-3xl font-extrabold text-white sm:text-4xl">{title}</h2>
      <p className="mt-2 max-w-sm text-sm text-slate-300 sm:text-base">{text}</p>
      <span className="absolute right-8 top-8 grid h-12 w-12 place-items-center rounded-full border border-white/15 bg-white/5 text-white transition-transform duration-300 group-hover:rotate-45">
        <ArrowUpRight size={20} />
      </span>
    </div>
  </Link>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [sum, setSum] = useState(null);
  const [txs, setTxs] = useState([]);
  useEffect(() => {
    api.get("/me/summary").then((r) => setSum(r.data)).catch(() => {});
    api.get("/payments/mine").then((r) => setTxs(r.data.slice(0, 5))).catch(() => {});
  }, []);

  return (
    <div className="space-y-12">
      <div className="rise">
        <p className="eyebrow">Dashboard</p>
        <h1 className="mt-2 text-3xl font-bold text-white sm:text-4xl" data-testid="dashboard-welcome">Hello, {user?.full_name?.split(" ")[0]}.</h1>
        <p className="mt-2 text-slate-400">What would you like to do today?</p>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rise d1"><BigCard to="/store" img={STORE_IMG} eyebrow="Digital store" title="BUY PRODUCTS" accent="text-emerald-400" testId="buy-products-card" text="Browse and purchase digital products from Raphanex." /></div>
        <div className="rise d2"><BigCard to="/predictions" img={PRED_IMG} eyebrow="Separate service" title="GET PREDICTIONS NOW" accent="text-amber-400" testId="get-predictions-card" text="Access football predictions and correct-score packages." /></div>
      </div>
      <div className="grid gap-5 sm:grid-cols-3">
        {[[ShoppingBag, "Purchases", sum?.purchases, "/purchases", "stat-purchases"], [CreditCard, "Payments", sum?.payments, "/payments", "stat-payments"], [Trophy, "Active prediction access", sum?.active_prediction_access, "/predictions", "stat-access"]].map(([I, l, v, to, tid]) => (
          <Link key={l} to={to} className="card-rx card-hover p-6" data-testid={tid}>
            <I size={20} className="text-slate-400" />
            <p className="mt-4 font-mono-rx text-3xl font-semibold text-white">{v ?? "–"}</p>
            <p className="mt-1 text-sm text-slate-400">{l}</p>
          </Link>
        ))}
      </div>
      <div>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">Recent payments</h2>
          <Link to="/payments" className="text-sm text-emerald-300 hover:text-emerald-200">View all</Link>
        </div>
        <div className="mt-5">
          {txs.length === 0 ? <EmptyState icon={CreditCard} testId="dashboard-payments-empty" title="No payments yet" text="Your transactions will show up here after your first purchase." /> : (
            <div className="card-rx divide-y divide-white/[0.05]">
              {txs.map((t) => (
                <div key={t.reference} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                  <div><p className="font-medium text-white">{t.item_name}</p><p className="font-mono-rx text-xs text-slate-500">{t.reference} · {fmtDate(t.created_at, true)}</p></div>
                  <div className="flex items-center gap-4"><span className="font-mono-rx text-sm text-slate-200">{ghs(t.amount_minor)}</span><StatusBadge status={t.status} /></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
